import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Table, Input, Button, Space, Tabs, Card, Modal, DatePicker, Form } from 'antd';
import { SearchOutlined, DeleteOutlined, FilterOutlined, BookOutlined } from '@ant-design/icons';
import { useFetchAllFactures, useDeleteFacture } from '../../hooks/factureHooks';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const FactureList = ({ onSelectFacture, defaultActiveTab = 'notValidated', onOpenJournal }) => {
  // State management
  const [clientSearch, setClientSearch] = useState('');
  const [compteSearch, setCompteSearch] = useState('');
  const [refDevSearch, setRefDevSearch] = useState('');
  const [refBlSearch, setRefBlSearch] = useState('');
  const [refFacSearch, setRefFacSearch] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [sorter, setSorter] = useState(() => {
    // Initialize sort order based on active tab
    return {
      field: 'REF_FAC',
      order: defaultActiveTab === 'notValidated' ? 'ascend' : 'descend'
    };
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [factureToDelete, setFactureToDelete] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const tableRef = useRef(null);

  // Update sort order when tab changes
  useEffect(() => {
    setSorter({
      field: 'REF_FAC',
      order: activeTab === 'notValidated' ? 'ascend' : 'descend'
    });
  }, [activeTab]);

  // Combine query parameters for the hook
  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    clientSearch,
    compteSearch,
    refDevSearch,
    refBlSearch,
    refFacSearch,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    status: activeTab,
  }), [pagination.current, pagination.pageSize, clientSearch, compteSearch, refDevSearch, refBlSearch, refFacSearch, dateRange, sorter.field, sorter.order, activeTab]);

  const { factures, total, loading, error, refetch } = useFetchAllFactures(params);
  const { handleDelete, loading: deleteLoading, error: deleteError } = useDeleteFacture();

  // Delete handlers
  const onDeleteClick = (e, record) => {
    e.stopPropagation();
    setFactureToDelete(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await handleDelete(factureToDelete.id);
      setDeleteModalVisible(false);
      refetch();
    } catch (err) {
      console.error('Error deleting facture:', err);
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearAllFilters = () => {
    setClientSearch('');
    setCompteSearch('');
    setRefDevSearch('');
    setRefBlSearch('');
    setRefFacSearch('');
    setDateRange([null, null]);
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Column definitions
  const columns = [
    {
      title: activeTab === 'validated' ? 'Date Fac.' : 'Date Fac.',
      key: 'date',
      dataIndex: activeTab === 'validated' ? 'DATE_FAC' : 'DATE_FAC',
      sorter: true,
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Ref. Fac.',
      dataIndex: 'REF_FAC',
      key: 'REF_FAC',
      sorter: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Commence par"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setRefFacSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setRefFacSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setRefFacSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text, record) => (
        <span 
          style={{ 
            position: 'relative',
            fontWeight: hoveredRow === record.REF_FAC ? 'bold' : 'normal',
            transition: 'font-weight 0.3s ease'
          }}
        >
          {text}
          <span
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: hoveredRow === record.REF_FAC ? '100%' : '0%',
              height: '1px',
              backgroundColor: '#000',
              transition: 'width 0.3s ease'
            }}
          />
        </span>
      )
    },
    {
      title: 'Compte',
      dataIndex: 'COMPTE',
      key: 'COMPTE',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher Compte"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setCompteSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setCompteSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setCompteSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Client',
      dataIndex: 'CLIENT',
      key: 'CLIENT',
      width: 400,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher Client"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setClientSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setClientSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setClientSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Total TTC',
      dataIndex: 'MNT_TTC',
      key: 'MNT_TTC',
      render: value => value?.toFixed(2),
      sorter: true,
    },
    {
      title: 'Ref. Dev',
      dataIndex: 'REF_DEV',
      key: 'REF_DEV',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Commence par"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setRefDevSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setRefDevSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setRefDevSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Ref. BL',
      dataIndex: 'REF_BL',
      key: 'REF_BL',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Commence par"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setRefBlSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setRefBlSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setRefBlSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Remarque',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {!record.VALIDER && (
            <PermissionButton permissionModule="invoices" permissionAction="delete"
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => onDeleteClick(e, record)}
            >
              
            </PermissionButton>
          )}
          
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
  };

  const getRowStyle = (record) => {
    if (record.VALIDER) {
      return { 
        backgroundColor: '#f6ffed', // Light green for validated
        cursor: 'pointer'
      };
    }
    return { cursor: 'pointer' };
  };

  return (
    <div>
      <Card style={{ marginTop: 20, backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>
            Listes Des Factures 
          </h2>
          <Button 
            type="primary"
            icon={<BookOutlined />}
            onClick={onOpenJournal}
          >
            Journal des Factures
          </Button>
        </div>
        
        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="dateRange" label="Période">
            <RangePicker 
              onChange={handleDateChange}
              value={dateRange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
            />
          </Form.Item>
          <Form.Item>
            <Button 
              icon={<FilterOutlined />} 
              onClick={clearAllFilters}
            >
              Réinitialiser filtres
            </Button>
          </Form.Item>
        </Form>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Enregistrés" key="notValidated" />
          <TabPane tab="Validés" key="validated" />
        </Tabs>

        <Table
          ref={tableRef}
          columns={columns}
          dataSource={factures}
          rowKey="REF_FAC"
          loading={loading}
          pagination={{ ...pagination, total }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => onSelectFacture(record.id),
            onMouseEnter: () => setHoveredRow(record.REF_FAC),
            onMouseLeave: () => setHoveredRow(null),
            style: getRowStyle(record)
          })}
          size="small"
        />

        <Modal
          title="Confirmer la suppression"
          visible={deleteModalVisible}
          onOk={handleDeleteConfirm}
          onCancel={() => setDeleteModalVisible(false)}
          confirmLoading={deleteLoading}
          okText="Oui"
          cancelText="Non"
        >
          <p>Êtes-vous sûr de vouloir supprimer cette facture?</p>
          {deleteError && <p style={{ color: 'red' }}>{deleteError}</p>}
        </Modal>
      </Card>
    </div>
  );
};

export default FactureList;